import _ from 'lodash';
import { DEFAULT_CHART_PROPERTIES, DEFAULT_CHART_SPECIFIC, DEFAULT_CHART_VIZ_PROPERTIES, DEFAULT_GRIDLINES, DEFAULT_SUMMARIES_STATE, } from '../client-state-type';
import { AxisTypes, DataLabelFilterOperator, } from '../types/prism';
import { getValidConditionalFormatting } from './table-overrides-util';
function applyChartLegendOverrides(chartVizProps, legendProperties) {
    if (_.isNil(legendProperties)) {
        return chartVizProps;
    }
    const { showLegend, position } = legendProperties;
    const updatedChartVizProps = {
        ...chartVizProps,
        chartProperties: {
            ...DEFAULT_CHART_PROPERTIES,
            ...chartVizProps.chartProperties,
        },
    };
    if (!_.isNil(showLegend)) {
        updatedChartVizProps.chartProperties.showLegend = showLegend;
    }
    if (!_.isNil(position)) {
        updatedChartVizProps.chartProperties.legendPosition = position;
    }
    return updatedChartVizProps;
}
function applyChartDisplayOverrides(vizProps, displayProperties) {
    var _a;
    if (_.isNil(displayProperties)) {
        return vizProps;
    }
    const updatedChartVizProps = {
        ...vizProps,
        chartProperties: {
            ...DEFAULT_CHART_PROPERTIES,
            ...vizProps.chartProperties,
            chartSpecific: {
                ...DEFAULT_CHART_SPECIFIC,
                ...(_a = vizProps.chartProperties) === null || _a === void 0 ? void 0 : _a.chartSpecific,
            },
        },
    };
    const { summariesState, regressionLineProperties, gridLineProperties, } = displayProperties;
    if (!_.isNil(summariesState)) {
        updatedChartVizProps.chartProperties.chartSpecific.pivotSummariesState = {
            ...DEFAULT_SUMMARIES_STATE,
            ...updatedChartVizProps.chartProperties.chartSpecific
                .pivotSummariesState,
            ..._.pickBy(summariesState, (v, k) => !_.isNil(v) && k !== '__typename'),
        };
    }
    if (!_.isNil(regressionLineProperties) &&
        !_.isNil(regressionLineProperties.enabled)) {
        updatedChartVizProps.chartProperties.showLinearRegressionLine =
            regressionLineProperties.enabled;
    }
    if (!_.isNil(gridLineProperties)) {
        updatedChartVizProps.chartProperties.gridLines = {
            ...DEFAULT_GRIDLINES,
            ...updatedChartVizProps.chartProperties.gridLines,
            ..._.pickBy(gridLineProperties, (v, k) => !_.isNil(v) && k !== '__typename'),
        };
    }
    return updatedChartVizProps;
}
function applyChartColumnOverrides(vizProps, columnProperties, columns) {
    var _a, _b;
    if (_.isNil(columnProperties) || _.isEmpty(columnProperties)) {
        return vizProps;
    }
    const updatedChartVizProps = { ...vizProps };
    const updatedColumnProperties = [
        ...((_a = updatedChartVizProps.columnProperties) !== null && _a !== void 0 ? _a : []),
    ];
    const updatedSeriesColors = [...((_b = updatedChartVizProps.seriesColors) !== null && _b !== void 0 ? _b : [])];
    const validColumnIds = columns.map(col => col.column.id);
    const validColumnOverrides = columnProperties.filter(override => !_.isNil(override === null || override === void 0 ? void 0 : override.columnId) &&
        validColumnIds.includes(override.columnId) &&
        !_.isNil(override === null || override === void 0 ? void 0 : override.properties));
    validColumnOverrides.forEach(override => {
        var _a;
        const existingIndex = updatedColumnProperties.findIndex(cp => (cp === null || cp === void 0 ? void 0 : cp.columnId) === override.columnId);
        const validConditionalFormatting = getValidConditionalFormatting(override.properties.conditionalFormatting, validColumnIds);
        if (!_.isNil(validConditionalFormatting)) {
            if (existingIndex >= 0) {
                updatedColumnProperties[existingIndex] = {
                    ...updatedColumnProperties[existingIndex],
                    columnProperty: {
                        ...DEFAULT_CHART_VIZ_PROPERTIES,
                        ...(_a = updatedColumnProperties[existingIndex]) === null || _a === void 0 ? void 0 : _a.columnProperty,
                        conditionalFormatting: validConditionalFormatting,
                    },
                };
            }
            else {
                updatedColumnProperties.push({
                    __typename: 'ChartColumnPropertyMap',
                    columnId: override.columnId,
                    columnProperty: {
                        ...DEFAULT_CHART_VIZ_PROPERTIES,
                        conditionalFormatting: validConditionalFormatting,
                    },
                });
            }
        }
        if (!_.isNil(override.properties.color)) {
            const existingColorIndex = updatedSeriesColors.findIndex(sc => (sc === null || sc === void 0 ? void 0 : sc.serieName) === override.columnId);
            if (existingColorIndex >= 0) {
                updatedSeriesColors[existingColorIndex] = {
                    ...updatedSeriesColors[existingColorIndex],
                    color: override.properties.color,
                };
            }
            else {
                updatedSeriesColors.push({
                    __typename: 'SeriesColor',
                    serieName: override.columnId,
                    color: override.properties.color,
                });
            }
        }
    });
    updatedChartVizProps.columnProperties = updatedColumnProperties;
    updatedChartVizProps.seriesColors = updatedSeriesColors;
    return updatedChartVizProps;
}
const FILTER_OPERATOR_LABEL_MAP = {
    [DataLabelFilterOperator.FilterOperatorUnspecified]: '',
    [DataLabelFilterOperator.LessThan]: 'Less than',
    [DataLabelFilterOperator.GreaterThan]: 'Greater than',
    [DataLabelFilterOperator.LessThanOrEqualTo]: 'Less than or equal to',
    [DataLabelFilterOperator.GreaterThanOrEqualTo]: 'Greater than or equal to',
    [DataLabelFilterOperator.EqualTo]: 'Equal to',
    [DataLabelFilterOperator.NotEqualTo]: 'Not equal to',
};
function applyDataLabelFilter(customProps, filter) {
    var _a, _b;
    const parsed = customProps ? JSON.parse(customProps) : {};
    const advancedProperties = (_a = parsed.advancedProperties) !== null && _a !== void 0 ? _a : {};
    const dataLabels = (_b = advancedProperties.dataLabels) !== null && _b !== void 0 ? _b : {};
    const isValidFilter = !_.isNil(filter.value) &&
        !_.isNil(filter.operator) &&
        filter.operator !== DataLabelFilterOperator.FilterOperatorUnspecified;
    return JSON.stringify({
        ...parsed,
        advancedProperties: {
            ...advancedProperties,
            dataLabels: {
                ...dataLabels,
                filter: isValidFilter
                    ? {
                        value: filter.value,
                        operator: FILTER_OPERATOR_LABEL_MAP[filter.operator],
                    }
                    : null,
            },
        },
    });
}
function applyChartDataLabelOverrides(vizProps, dataLabelProperties) {
    if (_.isNil(dataLabelProperties) || _.isEmpty(dataLabelProperties)) {
        return vizProps;
    }
    const updatedChartVizProps = {
        ...vizProps,
        chartProperties: {
            ...DEFAULT_CHART_PROPERTIES,
            ...vizProps.chartProperties,
        },
    };
    if (!_.isNil(dataLabelProperties.stackLabelsProperties)) {
        if (!_.isNil(dataLabelProperties.stackLabelsProperties.isVisible)) {
            updatedChartVizProps.chartProperties.showStackedLabels =
                dataLabelProperties.stackLabelsProperties.isVisible;
        }
    }
    if (!_.isNil(dataLabelProperties.columnDataLabelProperties)) {
        const columnDataLabelOverrides = dataLabelProperties.columnDataLabelProperties;
        updatedChartVizProps.columnProperties = updatedChartVizProps.columnProperties.map(cp => {
            const datalabelOverride = columnDataLabelOverrides.find(o => (o === null || o === void 0 ? void 0 : o.columnId) === cp.columnId);
            if (_.isNil(datalabelOverride) ||
                _.isNil(datalabelOverride.properties)) {
                return cp;
            }
            let updatedColumnProperty = { ...cp.columnProperty };
            if (!_.isNil(datalabelOverride.properties.isVisible)) {
                updatedColumnProperty = {
                    ...DEFAULT_CHART_VIZ_PROPERTIES,
                    ...updatedColumnProperty,
                    dataLabels: datalabelOverride.properties.isVisible,
                };
            }
            if (!_.isNil(datalabelOverride.properties.filter)) {
                updatedColumnProperty = {
                    ...updatedColumnProperty,
                    customProps: applyDataLabelFilter(updatedColumnProperty.customProps, datalabelOverride.properties.filter),
                };
            }
            return { ...cp, columnProperty: updatedColumnProperty };
        });
    }
    if (!_.isNil(dataLabelProperties.allLabels)) {
        updatedChartVizProps.chartProperties.allLabels =
            dataLabelProperties.allLabels;
        if (dataLabelProperties.allLabels) {
            updatedChartVizProps.chartProperties.showStackedLabels = true;
            updatedChartVizProps.columnProperties = updatedChartVizProps.columnProperties.map(cp => ({
                ...cp,
                columnProperty: {
                    ...DEFAULT_CHART_VIZ_PROPERTIES,
                    ...cp.columnProperty,
                    dataLabels: true,
                },
            }));
        }
    }
    return updatedChartVizProps;
}
function applyAxisVisibilityToCustomProps(customProps, overrideProps) {
    var _a, _b, _c, _d;
    const parsed = customProps ? JSON.parse(customProps) : {};
    const advancedProperties = (_a = parsed.advancedProperties) !== null && _a !== void 0 ? _a : {};
    const axisSettings = (_b = advancedProperties.axisSettings) !== null && _b !== void 0 ? _b : {};
    const axisName = (_c = axisSettings.axisName) !== null && _c !== void 0 ? _c : {};
    const labels = (_d = advancedProperties.labels) !== null && _d !== void 0 ? _d : {};
    const updatedAdvancedProperties = { ...advancedProperties };
    if (!_.isNil(overrideProps.isNameVisible)) {
        updatedAdvancedProperties.axisSettings = {
            ...axisSettings,
            axisName: {
                ...axisName,
                isAxisNameVisible: overrideProps.isNameVisible,
            },
        };
    }
    if (!_.isNil(overrideProps.isLabelVisible)) {
        updatedAdvancedProperties.labels = {
            ...labels,
            enabled: overrideProps.isLabelVisible,
        };
    }
    return JSON.stringify({
        ...parsed,
        advancedProperties: updatedAdvancedProperties,
    });
}
function applyChartAxisOverrides(vizProps, axisProperties) {
    if (_.isNil(axisProperties) || _.isEmpty(axisProperties)) {
        return vizProps;
    }
    const updatedChartVizProps = { ...vizProps };
    updatedChartVizProps.axisProperties = updatedChartVizProps.axisProperties.map((axisObj) => {
        var _a, _b, _c;
        const existingLinkedCols = new Set((_b = (_a = axisObj.properties) === null || _a === void 0 ? void 0 : _a.linkedColumns) !== null && _b !== void 0 ? _b : []);
        const matchingOverride = axisProperties.find(override => {
            var _a;
            const overrideLinkedCols = (_a = override.linkedColumns) !== null && _a !== void 0 ? _a : [];
            if (overrideLinkedCols.length !== existingLinkedCols.size) {
                return false;
            }
            return overrideLinkedCols.every(col => existingLinkedCols.has(col));
        });
        if (_.isNil(matchingOverride) ||
            _.isNil(matchingOverride.properties)) {
            return axisObj;
        }
        const overrideProps = matchingOverride.properties;
        const updatedAxisObj = {
            ...axisObj,
            properties: { ...axisObj.properties },
        };
        if (!_.isNil(overrideProps.isNameVisible) ||
            !_.isNil(overrideProps.isLabelVisible)) {
            updatedAxisObj.properties.customProps = applyAxisVisibilityToCustomProps(updatedAxisObj.properties.customProps, overrideProps);
        }
        if (!_.isNil(overrideProps.yAxisRange) &&
            ((_c = axisObj.properties) === null || _c === void 0 ? void 0 : _c.axisType) === AxisTypes.Y) {
            updatedAxisObj.properties.yAxisRange = overrideProps.yAxisRange;
        }
        return updatedAxisObj;
    });
    return updatedChartVizProps;
}
export function applyChartVisualOverrides(vizModel, columns) {
    const vizProps = vizModel.vizProps;
    const visualOverrides = vizModel.visualOverrides;
    if (_.isNil(visualOverrides) || _.isNil(visualOverrides.overrides)) {
        return vizProps;
    }
    const { legendProperties, displayProperties, columnProperties, dataLabelProperties, axisProperties, } = visualOverrides.overrides;
    let updatedChartVizProps = _.cloneDeep(vizProps);
    updatedChartVizProps = applyChartLegendOverrides(updatedChartVizProps, legendProperties);
    updatedChartVizProps = applyChartDisplayOverrides(updatedChartVizProps, displayProperties);
    updatedChartVizProps = applyChartColumnOverrides(updatedChartVizProps, columnProperties, columns);
    updatedChartVizProps = applyChartDataLabelOverrides(updatedChartVizProps, dataLabelProperties);
    updatedChartVizProps = applyChartAxisOverrides(updatedChartVizProps, axisProperties);
    return updatedChartVizProps;
}
//# sourceMappingURL=chart-overrides-util.js.map